import type { SVGProps } from "react";
import { memo } from "react";

import { cn } from "@btnet/utilities-core";

import "./EnrichedIcon.less";
import type { IEnrichedIconProps } from "./EnrichedIcon.types";

const SvgIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    fill="none"
    viewBox="0 0 116 116"
    {...props}
  >
    <path
      fill="#F1F4FA"
      d="M58 109c28.167 0 51-22.833 51-51 0-28.166-22.833-51-51-51C29.834 7 7 29.834 7 58c0 28.167 22.834 51 51 51"
    />
    <path
      fill="#C7D0D9"
      d="M11.57 93.2c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6m0-10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4"
    />
    <path
      fill="#ACB8C3"
      d="M94.43 95.41a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M6.57 71.57a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M82.36 11.26l3.94-3.94c.59-.59.59-1.54 0-2.12s-1.54-.59-2.12 0l-3.94 3.94L76.3 5.2a1.49 1.49 0 0 0-2.12 0c-.58.59-.59 1.54 0 2.12l3.94 3.94-3.94 3.94a1.49 1.49 0 0 0 0 2.12c.29.29.68.44 1.06.44s.77-.15 1.06-.44l3.94-3.94 3.94 3.94c.29.29.68.44 1.06.44s.77-.15 1.06-.44c.59-.59.59-1.54 0-2.12z"
    />
    <path
      fill="#C7D0D9"
      d="M106.07 73.63h-5.57v-5.57c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v5.57h-5.57c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5h5.57v5.57c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-5.57h5.57c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5M90.32 51.45h-5.06c-.76 0-1.46-.44-1.78-1.13l-2.15-4.6c-.36-.76-.19-1.66.41-2.24l3.57-3.46c.79-.76.8-2.02.02-2.79l-6.34-6.37c-.75-.75-1.97-.77-2.74-.04l-3.66 3.47c-.58.54-1.42.69-2.14.37l-4.82-2.14a1.96 1.96 0 0 1-1.17-1.79v-4.94c0-1.08-.88-1.96-1.96-1.96h-9.26c-1.08 0-1.96.88-1.96 1.96v5.2c0 .79-.47 1.5-1.19 1.81l-4.48 1.92c-.69.3-1.5.17-2.06-.33l-3.64-3.18c-.77-.67-1.93-.64-2.66.07l-6.45 6.26c-.79.77-.79 2.03 0 2.81l3.34 3.28c.58.57.75 1.44.42 2.18l-2.13 4.9c-.31.72-1.02 1.18-1.8 1.18h-4.94c-1.08 0-1.96.88-1.96 1.96v9.29c0 1.08.88 1.96 1.96 1.96h5.07c.76 0 1.45.44 1.78 1.12l2.24 4.73c.36.75.2 1.65-.4 2.24l-3.25 3.19c-.77.76-.78 2-.03 2.77l6.11 6.23c.75.76 1.97.79 2.74.06l3.54-3.31c.6-.56 1.48-.69 2.21-.33l4.51 2.23c.66.33 1.08 1 1.09 1.74l.05 4.5c.01 1.07.89 1.94 1.96 1.94h9.35c1.09 0 1.97-.89 1.96-1.99l-.05-4.68c-.01-.81.48-1.54 1.23-1.84l5.17-2.09c.74-.3 1.59-.12 2.15.45l3.22 3.33c.77.79 2.04.8 2.81 0l6.22-6.35c.76-.77.75-2.01-.03-2.77l-3.35-3.29c-.59-.58-.75-1.47-.4-2.22l2.35-5.07c.32-.69 1.02-1.14 1.78-1.14h4.61c1.08 0 1.96-.88 1.96-1.96v-9.17c0-1.08-.88-1.96-1.96-1.96zM58 67.87c-5.45 0-9.88-4.42-9.88-9.88s4.42-9.88 9.88-9.88 9.87 4.42 9.87 9.88-4.42 9.88-9.87 9.88"
    />
  </svg>
);
const SvgEnrichedIconSettingsGreyscale = ({
  testid,
  size = "md",
  className,
  ariaLabel,
  innerRef,
  ...props
}: IEnrichedIconProps) => {
  const ariaVisibilityProp = ariaLabel
    ? {
        "aria-label": ariaLabel,
      }
    : {
        "aria-hidden": true,
      };
  return (
    <div
      data-testid={testid}
      className={cn("enriched-icon bds-enriched-icon", className, {
        ["bds-enriched-icon-" + size]: size,
        ["enriched-icon-" + size]: size,
      })}
      ref={innerRef}
      {...props}
    >
      <SvgIcon role="img" {...ariaVisibilityProp} />
    </div>
  );
};
const Memo = memo(SvgEnrichedIconSettingsGreyscale);
export default Memo;
